import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Message, Modal } from 'semantic-ui-react';
import { getUpstreamModelSuggestionsV820 } from './channelModelVisibilityV820';
import {
  exportModelRulesBundleV822,
  MODEL_RULES_MAX_BYTES_V822,
  parseModelRulesBundleV822,
  planModelRulesImportV822,
} from './channelModelRulesV822';

const ChannelModelRulesTransferV822 = ({
  rows,
  selectedIds,
  onSelectAll,
  channelModels,
  modelIdPrefix,
  onChange,
  t,
  activation = false,
}) => {
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState('');
  const [importedRows, setImportedRows] = useState([]);
  const [decisions, setDecisions] = useState(Object.create(null));
  const readGeneration = useRef(0);
  const nextImportId = useRef(0);
  const nativeModels = useMemo(
    () => getUpstreamModelSuggestionsV820(channelModels, modelIdPrefix),
    [channelModels, modelIdPrefix]
  );
  const plan = useMemo(
    () =>
      planModelRulesImportV822(
        importedRows,
        rows,
        channelModels,
        modelIdPrefix,
        decisions
      ),
    [importedRows, rows, channelModels, modelIdPrefix, decisions]
  );

  useEffect(
    () => () => {
      readGeneration.current += 1;
    },
    []
  );

  const close = () => {
    readGeneration.current += 1;
    setOpen(false);
    setReading(false);
    setImportedRows([]);
    setDecisions(Object.create(null));
    setError('');
  };

  const readFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const generation = ++readGeneration.current;
    setImportedRows([]);
    setDecisions(Object.create(null));
    setError('');
    setReading(false);
    if (!file) return;
    if (file.size > MODEL_RULES_MAX_BYTES_V822) {
      setError('file_too_large');
      return;
    }
    setReading(true);
    try {
      const text = await file.text();
      if (generation !== readGeneration.current) return;
      setImportedRows(parseModelRulesBundleV822(text));
    } catch (failure) {
      if (generation === readGeneration.current) {
        setError(
          [
            'invalid_json',
            'invalid_bundle',
            'invalid_rule',
            'rule_limit',
            'file_too_large',
          ].includes(failure.message)
            ? failure.message
            : 'file_read_failed'
        );
      }
    } finally {
      if (generation === readGeneration.current) setReading(false);
    }
  };

  const updateDecision = (rowId, field, value) => {
    setDecisions((current) => ({
      ...current,
      [rowId]: { ...current[rowId], [field]: value },
    }));
  };

  const exportSelected = () => {
    setError('');
    let url;
    try {
      const text = exportModelRulesBundleV822(
        rows,
        selectedIds,
        channelModels,
        modelIdPrefix
      );
      url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'alfredapi-model-rules.json';
      anchor.click();
    } catch (failure) {
      setError(
        [
          'rule_limit',
          'file_too_large',
          'incomplete',
          'invalid_name',
          'invalid_resolution',
          'same_name',
          'duplicate_public_name',
          'upstream_not_declared',
          'hidden_target_is_public_name',
          'visibility_conflict',
          'native_alias_conflict',
        ].includes(failure.message)
          ? failure.message
          : 'export_failed'
      );
    } finally {
      if (url) URL.revokeObjectURL(url);
    }
  };

  const applyImport = () => {
    if (!plan.canApply || reading) return;
    const additions = plan.additions.map((row) => ({
      id: `mapping-import-${Date.now()}-${++nextImportId.current}`,
      upstreamModel: row.upstreamModel,
      publicModel: row.publicModel,
      resolution: row.resolution,
      targetOnly: row.targetOnly,
      enabled: row.enabled !== false,
    }));
    onChange([...rows, ...additions]);
    close();
  };

  return (
    <div className='channel-model-rules-transfer-v822'>
      <Button
        type='button'
        size='small'
        onClick={onSelectAll}
        disabled={!rows.length}
      >
        {t(
          activation
            ? `channel.edit.model_rules_v107.${
                rows.every((row) => row.enabled !== false)
                  ? 'disable_all'
                  : 'enable_all'
              }`
            : 'channel.edit.model_rules_v822.select_all'
        )}
      </Button>
      <Button
        type='button'
        size='small'
        disabled={!selectedIds.length}
        onClick={exportSelected}
      >
        {t(
          activation
            ? 'channel.edit.model_rules_v107.export_rules'
            : 'channel.edit.model_rules_v822.export_selected',
          {
            count: selectedIds.length,
          }
        )}
      </Button>
      <Button
        type='button'
        size='small'
        onClick={() => {
          setError('');
          setOpen(true);
        }}
      >
        {t('channel.edit.model_rules_v822.import')}
      </Button>
      {!open && error && (
        <Message negative>
          {t(`channel.edit.model_rules_v822.errors.${error}`)}
        </Message>
      )}
      {open && (
        <Modal open onClose={close} size='large'>
          <Modal.Header>
            {t('channel.edit.model_rules_v822.import_title')}
          </Modal.Header>
          <Modal.Content scrolling>
            <Message info>
              {t('channel.edit.model_rules_v822.import_notice')}
            </Message>
            {!nativeModels.length && (
              <Message warning>
                {t('channel.edit.model_rules_v822.no_native')}
              </Message>
            )}
            <label className='channel-model-rules-v822__file'>
              {t('channel.edit.model_rules_v822.choose_file')}
              <input
                type='file'
                accept='.json,application/json'
                onChange={readFile}
              />
            </label>
            {reading && (
              <Message role='status'>
                {t('channel.edit.model_rules_v822.reading')}
              </Message>
            )}
            {error && (
              <Message negative role='alert'>
                {t(`channel.edit.model_rules_v822.errors.${error}`)}
              </Message>
            )}
            {plan.entries.map(({ row, error: rowError, skipped }) => (
              <div
                key={row.id}
                className='channel-model-rules-v822__import-row'
              >
                <label>
                  {t('channel.edit.model_mapping_upstream')}
                  <select
                    aria-label={t('channel.edit.model_rules_v822.rebind')}
                    value={
                      nativeModels.includes(row.upstreamModel)
                        ? row.upstreamModel
                        : ''
                    }
                    disabled={skipped}
                    onChange={(event) =>
                      updateDecision(
                        row.id,
                        'upstreamModel',
                        event.target.value
                      )
                    }
                  >
                    <option value='' disabled>
                      {t('channel.edit.model_rules_v822.missing_native', {
                        model: row.upstreamModel,
                      })}
                    </option>
                    {nativeModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('channel.edit.model_mapping_public')}
                  <input
                    value={row.publicModel}
                    disabled={skipped}
                    onChange={(event) =>
                      updateDecision(row.id, 'publicModel', event.target.value)
                    }
                  />
                </label>
                <span>
                  {t('channel.edit.model_rules_v822.resolution')}:{' '}
                  {row.resolution === '512'
                    ? '0.5K'
                    : row.resolution ||
                      t('channel.edit.model_rules_v822.unconstrained')}
                </span>
                <label>
                  <input
                    type='checkbox'
                    checked={Boolean(row.targetOnly)}
                    disabled={skipped}
                    onChange={(event) =>
                      updateDecision(row.id, 'targetOnly', event.target.checked)
                    }
                  />
                  {t('channel.edit.model_mapping_hide_upstream')}
                </label>
                <label>
                  <input
                    type='checkbox'
                    checked={skipped}
                    onChange={(event) =>
                      updateDecision(row.id, 'skip', event.target.checked)
                    }
                  />
                  {t('channel.edit.model_rules_v822.skip')}
                </label>
                <div role='status'>
                  {t(
                    `channel.edit.model_rules_v822.${
                      skipped
                        ? 'skipped'
                        : rowError
                        ? `errors.${rowError}`
                        : 'ready'
                    }`
                  )}
                </div>
              </div>
            ))}
          </Modal.Content>
          <Modal.Actions>
            <Button type='button' onClick={close}>
              {t('channel.edit.buttons.cancel')}
            </Button>
            <Button
              type='button'
              positive
              disabled={!plan.canApply || reading}
              onClick={applyImport}
            >
              {t('channel.edit.model_rules_v822.apply_draft', {
                count: plan.additions.length,
              })}
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </div>
  );
};

export default ChannelModelRulesTransferV822;
